import _ from 'lodash';
import asyncFilter from 'async/filter';

const INITIAL_PROPERTIES = [
    'address',
    'online',
    'offline_message',
    'order_methods',
    'customer_message_html',
    'opening_hours',
    'wait_times',
    'current_time',
    'accept_orders_until_closed',
    'delivery',
    'delivery_areas',
    'delivery_minimum_order_products_total',
    'payment_methods',
    'menus',
    'status',
    'offline_message',
    'menu_restrictions',
    'trading_name',
    'has_tax_exclusive_prices',
    'sales_tax',
    'delivery_sales_tax',
    'tips_sales_tax',
    'has_tips_enabled',
    'surcharge_sales_tax',
    'discount_sales_tax',
    'tax_number',
    'order_scheduled_expected_at_intervals',
    'order_asap_expected_at_intervals',
    'scheduled_order_dates_limit',
    'is_storefront_locked_out',
    'menu_tier_id',
    'holidays',
    'group_tabs_enabled',
    'pay_at_table_enabled',
    'pay_at_table_configured',
];
let ready = false;
let load_retries = 0;

const Locations = Mobi2Go._class.Common.Collection([]);

Mobi2Go.Locations = Locations;

/*------------------------------------------------------------------------------
 * Inititate
 */
let preload_timeout = null;
function load() {
    // Locations data may have been preloaded in the page by a separate mechanism
    if (window.__mobi2go_preload_locations !== undefined) {
        if (window.__mobi2go_preload_locations === null) {
            setTimeout(load, 20);

            // Fallback to default load strategy after 10 seconds
            if (preload_timeout === null) {
                preload_timeout = setTimeout(() => {
                    delete window.__mobi2go_preload_locations;
                }, 1000 * 10);
            }
            return;
        }
        Locations.set(window.__mobi2go_preload_locations);
        return;
    }

    Mobi2Go.app.serverRequest({
        endpoint: 'locations',
        api_version: 1,
        with_credentials: false,
        from_cdn: Mobi2Go.app.feature_flags?.load_locations_from_cdn,
        data: {
            fields: INITIAL_PROPERTIES.join(','),
            include_hidden: true,
        },
        success(data) {
            Locations.set(data);
        },
        error: function handleError() {
            // Try again a few times
            if (load_retries < 10) {
                load_retries += 1;
                setTimeout(load, 500);
                return true;
            }
            return false;
        },
    });
}

setTimeout(load, 0);

/*------------------------------------------------------------------------------
 */
Locations.set = function set(locations_data) {
    const { Location } = Mobi2Go._class;

    if (Location === undefined) {
        _.delay(() => {
            Locations.set(locations_data);
        }, 30);
        return;
    }

    this.collection = _.map(locations_data, Location);

    ready = true;
    this.trigger('ready');
};

/*------------------------------------------------------------------------------
 * Add events
 */
Mobi2Go.app.mixinEvents('Locations', Locations);

/*------------------------------------------------------------------------------
 * Bind a function to be fired when locations collection has finished loading
 */
Locations.onReady = function onReady(callback) {
    if (ready) {
        if (_.isFunction(callback)) {
            _.defer(callback);
        }
    } else {
        Locations.once('ready', callback);
    }
};

/*------------------------------------------------------------------------------
 * Return locations by region
 */
Locations.getByRegion = function getByRegion({
    include_hidden_and_inactive = false,
} = {}) {
    const list = include_hidden_and_inactive
        ? this.get()
        : this.getAllNotHidden();

    return this.groupByRegion(list);
};

/*------------------------------------------------------------------------------
 * Returns closest non-hidden location
 */
Locations.getClosestToCustomer = function getClosestToCustomer() {
    const locations = this.getAllNotHidden();
    const has_user_coords = Mobi2Go.MapsService.coordinates;

    if (locations.length === 0 || !has_user_coords) {
        return null;
    }

    const locations_by_distance = _.sortBy(
        locations,
        location => location.distance_to_customer
    );

    return locations_by_distance[0];
};

/*------------------------------------------------------------------------------
 */
Locations.groupByRegion = function groupByRegion(list) {
    const locations_by_region = {};

    _.each(list, location => {
        const { region } = location.address;
        if (_.isUndefined(locations_by_region[region])) {
            locations_by_region[region] = [];
        }

        locations_by_region[region].push(location);
    });

    return locations_by_region;
};

/*------------------------------------------------------------------------------
 */
Locations.getFirstNotHidden = function getFirstNotHidden() {
    return this.find(location => location.status !== 'hidden');
};

/*------------------------------------------------------------------------------
 */
Locations.hasOnlyOne = function hasOnlyOne() {
    return this.getAllNotHidden().length === 1;
};

/*------------------------------------------------------------------------------
 */
Locations.getAllNotHidden = function getAllNotHidden() {
    return this.filter(location => location.status !== 'hidden');
};

/*------------------------------------------------------------------------------
 * 'Location First Flow' means customers are encouraged to first select a store
 * before adding items from the menu to their order.
 *
 * There are two situations where the Location First flow is optimal:
 *
 * - Locations have multiple separate menus.
 * - Locations have many restricted products.
 *
 * If customers add items to their order without choosing a store in these
 * situations, there is a chance that items in their menu will be
 * unavailable when selecting their store due to either the items not existing
 * in the location's menu, or being restricted at the location.
 *
 * To avoid this frustration, this method determines whether to force the
 * 'location-first' flow.
 */
Locations.hasLocationFirstOrderFlow = function hasLocationFirstOrderFlow() {
    const override = Mobi2Go.headoffice.location_first_flow;
    if (override !== undefined && override !== null) {
        return !!override;
    }

    if (this.getLength() > 1) {
        if (this.getDistinctMenuCount() > 1) {
            return true;
        }
    }

    if (this.getMaximumCountOfRestrictedMenuItems() >= 5) {
        return true;
    }

    return false;
};

/*------------------------------------------------------------------------------
 * Returns the highest count of restricted products across all locations
 */
Locations.getMaximumCountOfRestrictedMenuItems = function getMaximumCountOfRestrictedMenuItems() {
    const counts = _.map(
        this.getAllNotHidden(),
        location => location.menu_restrictions.length,
        0
    );

    return _.max(counts);
};

/*------------------------------------------------------------------------------
 * Returns which order method should be selected by default based on order
 * methods available across locations
 */
Locations.getDefaultOrderMethod = function getDefaultOrderMethod() {
    let default_method = 'delivery';
    const pickup_count = this.getPickupAvailableCount();
    const delivery_count = this.getDeliveryAvailableCount();

    if (delivery_count === 0 || pickup_count > delivery_count) {
        default_method = 'pickup';
    }

    return default_method;
};

/*------------------------------------------------------------------------------
 * Returns number of active locations accepting pick up orders
 */
Locations.getPickupAvailableCount = function getPickupAvailableCount() {
    return this.search({ method: 'pickup', status: 'active' }).length;
};

/*------------------------------------------------------------------------------
 */
Locations.isPickupAvailable = function isPickupAvailable() {
    return this.getPickupAvailableCount() !== 0;
};

/*------------------------------------------------------------------------------
 * Returns number of active locations accepting delivery orders
 */
Locations.getDeliveryAvailableCount = function getDeliveryAvailableCount() {
    return this.search({ method: 'delivery', status: 'active' }).length;
};

/*------------------------------------------------------------------------------
 */
Locations.isDeliveryAvailable = function isDeliveryAvailable() {
    return this.getDeliveryAvailableCount() !== 0;
};

/*------------------------------------------------------------------------------
 * Returns number of active locations accepting delivery orders
 */
Locations.getEatinAvailableCount = function getEatinAvailableCount() {
    return this.search({ method: 'eatin', status: 'active' }).length;
};

/*------------------------------------------------------------------------------
 * Any active
 */
Locations.areAnyActive = function areAnyActive() {
    return !!this.search({ status: 'active' }).length;
};

/*------------------------------------------------------------------------------
 * Are all locations hidden?
 */
Locations.areAllHidden = function areAllHidden() {
    return this.search({ status: 'hidden' }).length === this.collection.length;
};

/*------------------------------------------------------------------------------
 */
Locations.isEatinAvailable = function isEatinAvailable() {
    return this.getEatinAvailableCount() !== 0;
};

/*------------------------------------------------------------------------------
 * Returns number of different menus
 */
Locations.getDistinctMenuCount = function getDistinctMenuCount() {
    const menu_ids = _.reduce(
        this.getAllNotHidden(),
        (result, location) => result.concat(_.map(location.menus, 'id')),
        []
    );

    return _.uniq(menu_ids).length;
};

/*------------------------------------------------------------------------------
 */
Locations.getClosestToPoint = function getClosestToPoint(point) {
    const locations = this.getAllNotHidden();
    return _.sortBy(locations, location =>
        location.getDistanceFromPoint(point)
    );
};

/**-----------------------------------------------------------------------------
 * Returns locations that are available to delivery to a specified address
 */

Locations.getAllWithDeliveryAvailableToAddressAsync = async function getAllWithDeliveryAvailableToAddressAsync(
    address,
    { include_hidden_and_inactive = false } = {}
) {
    const locations = this.search({
        status: include_hidden_and_inactive ? null : 'active',
    });

    const available_locations =
        (await asyncFilter(locations, async (location, callback) => {
            if (!location.order_methods.delivery) {
                callback(null, false);
                return;
            }
            if (
                location.delivery.mode !== 'door_dash_drive' &&
                !location.isAddressAvailable(address)
            ) {
                callback(null, false);
                return;
            }
            if (location.delivery.mode === 'door_dash_drive') {
                const estimate = await location.getDoorDashDriveEstimate(
                    address
                );
                if (!estimate) {
                    callback(null, false);
                    return;
                }
            }
            callback(null, true);
        })) || [];

    return this.rankLocationsForDelivery(available_locations, address);
};

/*------------------------------------------------------------------------------
 */
Locations.getAllWithDeliveryAvailableToPoint = function getAllWithDeliveryAvailableToPoint(
    point
) {
    const locations = this.search({
        status: 'active',
        delivery_mode: 'map_polygons',
        delivery_available_to_point: point,
    });

    return this.rankLocationsForDelivery(locations, point);
};

/*------------------------------------------------------------------------------
 */
Locations.getAllWithPickUpOrEatinAvailable = function getAllWithPickUpOrEatinAvailable({
    include_hidden_and_inactive = false,
} = {}) {
    const locations = this.filter(
        location =>
            (location.order_methods.pickup || location.order_methods.eatin) &&
            (include_hidden_and_inactive || !location.isHidden())
    );

    return this.rankLocationsForPickUpOrEatIn(locations);
};

/**-----------------------------------------------------------------------------
 * Rank location suggestions with the priorities:
 * - Stores that are open
 * - Stores that are closest to delivery address
 * - Stores that have the lowest delivery wait time if stores
 *   have less than 100m difference in distance between the store
 *   and the delivery address
 */
Locations.rankLocationsForDelivery = function rankLocationsForDelivery(
    locations,
    address
) {
    if (!locations) {
        throw TypeError('locations list required');
    }

    if (
        !address ||
        address.latitude === undefined ||
        address.longitude === undefined
    ) {
        throw TypeError(
            'address object with "latitude" and "longitude" properties required'
        );
    }

    // Sort locations
    return locations.sort((a_location, b_location) => {
        const a_minutes_to_asap_order = a_location.getMinutesToAsapOrderDatetime(
            'delivery'
        );
        const b_minutes_to_asap_order = b_location.getMinutesToAsapOrderDatetime(
            'delivery'
        );
        const a_is_open =
            a_location.isOpenForMethod('delivery') &&
            a_minutes_to_asap_order !== 0 &&
            a_minutes_to_asap_order < 120;
        const b_is_open =
            b_location.isOpenForMethod('delivery') &&
            b_minutes_to_asap_order !== 0 &&
            b_minutes_to_asap_order < 120;

        // Sort by open status
        if (!a_is_open && b_is_open) {
            return 1;
        }

        if (!b_is_open && a_is_open) {
            return -1;
        }

        const a_distance_to_address = a_location.getDistanceFromPoint(address);
        const b_distance_to_address = b_location.getDistanceFromPoint(address);
        const distance_delta = Math.abs(
            a_distance_to_address - b_distance_to_address
        );
        const distance_threshold = 0.1; // 100M

        // Sort by distance
        if (distance_delta > distance_threshold) {
            if (a_distance_to_address < b_distance_to_address) {
                return -1;
            }
            return 1;
        }

        // Sort by wait time
        if (a_minutes_to_asap_order < b_minutes_to_asap_order) {
            return -1;
        }
        return 1;
    });
};

/*------------------------------------------------------------------------------
 */
Locations.rankLocationsForPickUpOrEatIn = function rankLocationsForPickUpOrEatIn(
    locations
) {
    const locations_by_distance = _.sortBy(
        locations,
        location => location.distance_to_customer
    );
    const should_sort_by_distance =
        locations_by_distance.length &&
        locations_by_distance[0].distance_to_customer !== null &&
        locations_by_distance[0].distance_to_customer < 50;

    if (should_sort_by_distance) {
        return locations.sort((a_location, b_location) => {
            const a_wait_time = a_location.wait_times.pickup;
            const b_wait_time = b_location.wait_times.pickup;

            if (
                a_location.distance_to_customer ||
                b_location.distance_to_customer
            ) {
                if (
                    a_location.distance_to_customer <
                    b_location.distance_to_customer
                ) {
                    return -1;
                }

                if (
                    a_location.distance_to_customer >
                    b_location.distance_to_customer
                ) {
                    return 1;
                }
            }

            // Sort by wait time
            if (a_wait_time < b_wait_time) {
                return -1;
            }
            return 1;
        });
    }

    return locations;
};

/*-----------------------------------------------------------------------------
 * Maximum distance from a customer where a pick up location will be suggested
 */
Locations.PICKUP_DISTANCE_THRESHOLD = 50;

/*------------------------------------------------------------------------------
 * Return list of near by locations that are currently suitable for pick up orders
 * for a customer
 */
Locations.getSuggestionsForPickup = function getSuggestionsForPickup({
    include_hidden_and_inactive = false,
} = {}) {
    let count = 0;
    const limit = 4;
    return _.filter(
        this.getAllWithPickUpOrEatinAvailable({ include_hidden_and_inactive }),
        location => {
            if (count === limit) {
                return false;
            }

            const is_valid =
                location.distance_to_customer !== null &&
                location.distance_to_customer <
                    Locations.PICKUP_DISTANCE_THRESHOLD;
            if (is_valid) {
                count += 1;
            }
            return is_valid;
        }
    );
};

/*------------------------------------------------------------------------------
 */
Locations.search = function search(provided_query) {
    const query = _.extend(
        {
            name: '',
            method: null,
            status: 'active',
            delivery_mode: null,
            delivery_available_to_point: null,
            delivery_available_to_address: null,
        },
        provided_query
    );

    return this.filter(location => {
        if (
            query.name &&
            !_.includes(location.name.toLowerCase(), query.name.toLowerCase())
        ) {
            return false;
        }

        if (query.method && !location.order_methods[query.method]) {
            return false;
        }

        if (query.status && location.status !== query.status) {
            return false;
        }

        if (
            query.delivery_mode &&
            location.delivery.mode !== query.delivery_mode
        ) {
            return false;
        }

        if (query.delivery_available_to_point) {
            if (
                !location.order_methods.delivery ||
                location.delivery.mode !== 'map_polygons' ||
                !location.isPointInDeliveryMapPolygons(
                    query.delivery_available_to_point
                )
            ) {
                return false;
            }
        }

        return true;
    });
};

/*--------------------------------------------------------------------------
 * Returns location grouped by delivery city, then suburb.
 *
 * e.g.
 * {
 *      "Auckland": {
 *          "Britomart": [Location, Location],
 *          "Ponsonby": [Location]
 *      },
 *      "Wellington": {
 *          "Te Aro": [Location, Location],
 *          "Te Aro": [Location, Location]
 *          "Te Aro": [Location, Location, Location]
 *      }
 * }
 */
Locations.getGroupedByDeliverySuburb = function getGroupedByDeliverySuburb() {
    const delivery_suburb_locations = {};

    this.each(location => {
        const areas = location.delivery_areas.suburbs || [];

        _.each(areas, area => {
            if (delivery_suburb_locations[area.city] === undefined) {
                delivery_suburb_locations[area.city] = {};
            }

            if (
                delivery_suburb_locations[area.city][area.suburb] === undefined
            ) {
                delivery_suburb_locations[area.city][area.suburb] = [location];
            } else {
                delivery_suburb_locations[area.city][area.suburb].push(
                    location
                );
            }
        });
    });

    return delivery_suburb_locations;
};
